import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { UserCheck, FileText, CreditCard, CheckCircle, XCircle, Clock, Eye, X, RefreshCw, Search, Filter, Shield } from 'lucide-react';

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const KYC_STATUS = {
  not_submitted: { label: 'Non soumis', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: Clock },
  submitted: { label: 'Soumis', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  under_review: { label: 'En révision', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Eye },
  verified: { label: 'Vérifié', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle }
};

const StatusBadge = ({ status }) => {
  const cfg = KYC_STATUS?.[status] || KYC_STATUS?.not_submitted;
  const IconComp = cfg?.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg?.color}`}>
      <IconComp size={11} />
      {cfg?.label}
    </span>
  );
};

const ReviewModal = ({ submission, onClose, onAction, loading }) => {
  const [decision, setDecision] = useState('');
  const [note, setNote] = useState('');

  if (!submission) return null;

  const docs = submission?.documents || {};

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <UserCheck size={18} className="text-blue-700" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Révision KYC</h3>
              <p className="text-sm text-gray-500">{submission?.full_name || submission?.user_id?.slice(0, 8)}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Applicant Info */}
          <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
            <div>
              <p className="text-xs text-gray-500 font-medium">Nom complet</p>
              <p className="text-sm font-semibold text-gray-900 mt-0.5">{submission?.full_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Rôle</p>
              <p className="text-sm font-semibold text-gray-900 mt-0.5 capitalize">{submission?.role || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">NIF</p>
              <p className="text-sm font-semibold text-gray-900 mt-0.5 font-mono">{submission?.nif || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Soumis le</p>
              <p className="text-sm font-semibold text-gray-900 mt-0.5">{formatDate(submission?.submitted_at)}</p>
            </div>
          </div>

          {/* Documents */}
          <div>
            <h4 className="text-sm font-bold text-gray-700 mb-3">Documents soumis</h4>
            <div className="space-y-3">
              {[
                { key: 'id_document', label: "Pièce d'identité", icon: FileText },
                { key: 'nif_document', label: 'Justificatif NIF', icon: CreditCard },
                { key: 'bank_proof', label: 'Justificatif bancaire', icon: Shield }
              ]?.map(({ key, label, icon: DocIcon }) => (
                <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                      <DocIcon size={15} className="text-blue-700" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{label}</p>
                      <p className="text-xs text-gray-500">{docs?.[key] ? 'Document fourni' : 'Non fourni'}</p>
                    </div>
                  </div>
                  {docs?.[key] ? (
                    <a
                      href={docs?.[key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
                    >
                      <Eye size={12} />
                      Voir
                    </a>
                  ) : (
                    <span className="text-xs text-red-600 font-medium flex items-center gap-1">
                      <XCircle size={12} />
                      Manquant
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Decision */}
          <div>
            <h4 className="text-sm font-bold text-gray-700 mb-3">Décision</h4>
            <div className="flex gap-3 mb-3">
              <button
                onClick={() => setDecision('verified')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all ${decision === 'verified' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-green-300'}`}
              >
                <CheckCircle size={16} />
                Approuver
              </button>
              <button
                onClick={() => setDecision('rejected')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all ${decision === 'rejected' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600 hover:border-red-300'}`}
              >
                <XCircle size={16} />
                Rejeter
              </button>
              <button
                onClick={() => setDecision('under_review')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all ${decision === 'under_review' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:border-amber-300'}`}
              >
                <Eye size={16} />
                En révision
              </button>
            </div>
            <textarea
              value={note}
              onChange={e => setNote(e?.target?.value)}
              rows={3}
              placeholder="Note interne (motif de rejet, observations...)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>
        </div>

        <div className="p-5 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
            Annuler
          </button>
          <button
            onClick={() => onAction(submission?.id, decision, note)}
            disabled={!decision || loading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle size={15} />}
            Confirmer la décision
          </button>
        </div>
      </div>
    </div>
  );
};

const KYCVerificationModule = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ total: 0, pending: 0, verified: 0, rejected: 0 });

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase?.from('kyc_submissions')?.select('*')?.order('submitted_at', { ascending: false });

      const rows = data || [];
      setSubmissions(rows);
      setStats({
        total: rows?.length,
        pending: rows?.filter(r => ['submitted', 'under_review']?.includes(r?.status))?.length,
        verified: rows?.filter(r => r?.status === 'verified')?.length,
        rejected: rows?.filter(r => r?.status === 'rejected')?.length
      });
    } catch (err) {
      console.error('Error loading KYC submissions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSubmissions(); }, [loadSubmissions]);

  const handleAction = async (id, status, note) => {
    setActionLoading(true);
    try {
      await supabase?.from('kyc_submissions')?.update({
          status,
          review_note: note,
          reviewed_at: new Date()?.toISOString()
        })?.eq('id', id);

      // If verified, update user profile payout eligibility
      if (status === 'verified') {
        const sub = submissions?.find(s => s?.id === id);
        if (sub?.user_id) {
          await supabase?.from('user_profiles')?.update({ kyc_verified: true, kyc_verified_at: new Date()?.toISOString() })?.eq('id', sub?.user_id);
        }
      }

      setSelectedSubmission(null);
      await loadSubmissions();
    } catch (err) {
      console.error('Error updating KYC status:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = submissions?.filter(s => {
    const matchStatus = filterStatus === 'all' || s?.status === filterStatus;
    const matchSearch = !searchQuery || s?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) || s?.nif?.includes(searchQuery);
    return matchStatus && matchSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Module KYC — Vérification d'Identité</h2>
          <p className="text-sm text-gray-500 mt-0.5">Agents et notaires doivent soumettre leur KYC avant le premier paiement</p>
        </div>
        <button
          onClick={loadSubmissions}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          <RefreshCw size={15} />
          Actualiser
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total soumissions', value: stats?.total, color: 'bg-blue-100 text-blue-700', icon: FileText },
          { label: 'En attente révision', value: stats?.pending, color: 'bg-amber-100 text-amber-700', icon: Clock },
          { label: 'Vérifiés', value: stats?.verified, color: 'bg-green-100 text-green-700', icon: CheckCircle },
          { label: 'Rejetés', value: stats?.rejected, color: 'bg-red-100 text-red-700', icon: XCircle }
        ]?.map(({ label, value, color, icon: Ic }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 shadow-sm">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
              <Ic size={18} />
            </div>
            <div>
              <p className="text-xs text-gray-500">{label}</p>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3 shadow-sm">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par nom ou NIF..."
            value={searchQuery}
            onChange={e => setSearchQuery(e?.target?.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={15} className="text-gray-400" />
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e?.target?.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tous les statuts</option>
            <option value="submitted">Soumis</option>
            <option value="under_review">En révision</option>
            <option value="verified">Vérifiés</option>
            <option value="rejected">Rejetés</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">
            {[...Array(5)]?.map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filtered?.length === 0 ? (
          <div className="p-12 text-center">
            <UserCheck size={36} className="text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500">Aucune soumission KYC trouvée</p>
            <p className="text-xs text-gray-400 mt-1">Les agents et notaires soumettront leurs documents ici</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Nom</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Rôle</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">NIF</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Documents</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Soumis le</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered?.map(sub => {
                  const docs = sub?.documents || {};
                  const docCount = ['id_document', 'nif_document', 'bank_proof']?.filter(k => docs?.[k])?.length;
                  return (
                    <tr key={sub?.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-blue-700">{sub?.full_name?.charAt(0)?.toUpperCase() || '?'}</span>
                          </div>
                          <span className="font-medium text-gray-900">{sub?.full_name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-gray-600 capitalize">{sub?.role || '—'}</td>
                      <td className="px-5 py-3.5 font-mono text-gray-700 text-xs">{sub?.nif || '—'}</td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${docCount === 3 ? 'bg-green-100 text-green-700' : docCount > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                          {docCount}/3
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-gray-600 text-xs">{formatDate(sub?.submitted_at)}</td>
                      <td className="px-5 py-3.5 text-center">
                        <StatusBadge status={sub?.status} />
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <button
                          onClick={() => setSelectedSubmission(sub)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors border border-blue-200"
                        >
                          <Eye size={12} />
                          Réviser
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {selectedSubmission && (
        <ReviewModal
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
          onAction={handleAction}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default KYCVerificationModule;
